import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export const emptyProduct = {
  name: "", category: "02.03.00-noi", description: "", price: 0, stock: 0, image: "", brand: "", condition: "nou",
  specs: { motor_power_kw: "", spindle_diameter_mm: "", weight_kg: "", max_rpm: "", year: "" },
};

function categoryDepthClass(depth) {
  if (depth === 0) return "font-bold text-orange-600";
  if (depth === 1) return "pl-6 text-slate-700";
  return "pl-10 text-slate-500 text-xs";
}

export default function ProductForm({ initial, onSaved, cats }) {
  const [form, setForm] = useState(initial);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    const specs = {};
    for (const [k, v] of Object.entries(form.specs)) if (v !== "" && v != null) specs[k] = Number(v);
    const payload = { ...form, price: Number(form.price), stock: Number(form.stock), specs };
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Produs salvat");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSpec = (k) => (e) => setForm({ ...form, specs: { ...form.specs, [k]: e.target.value } });
  const setI18n = (field, lang) => (e) => setForm({
    ...form, [field]: { ...(form[field] || {}), [lang]: e.target.value },
  });
  const LANGS = [["ro", "Română"], ["hu", "Magyar"], ["en", "English"]];

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(initial); }}>
      <DialogTrigger asChild>
        {initial.id ? (
          <button className="p-2 hover:bg-slate-100 text-slate-600" data-testid={`edit-${initial.id}`}><Pencil className="w-4 h-4" /></button>
        ) : (
          <button className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="add-product-button">
            <Plus className="w-4 h-4" /> Produs nou
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form.id ? "Editează produs" : "Produs nou"}</DialogTitle>
          <DialogDescription>Completează detaliile produsului și specificațiile tehnice.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input placeholder="Nume produs" value={form.name} onChange={set("name")} className="rounded-none" data-testid="form-name" />
          <div className="grid grid-cols-2 gap-4">
            <Input placeholder="Marcă" value={form.brand} onChange={set("brand")} className="rounded-none" />
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-none"><SelectValue placeholder="Categorie" /></SelectTrigger>
              <SelectContent className="max-h-[50vh]">
                {(cats || []).map((c) => (
                  <SelectItem key={c.slug} value={c.slug} className={categoryDepthClass(c.depth)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea placeholder="Descriere" value={form.description} onChange={set("description")} className="rounded-none" />
          <Input placeholder="URL imagine" value={form.image} onChange={set("image")} className="rounded-none" />
          <div className="grid grid-cols-3 gap-4">
            <Input type="number" placeholder="Preț (RON)" value={form.price} onChange={set("price")} className="rounded-none" data-testid="form-price" />
            <Input type="number" placeholder="Stoc" value={form.stock} onChange={set("stock")} className="rounded-none" />
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="nou">Nou</SelectItem><SelectItem value="second-hand">Second-hand</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="text-xs uppercase tracking-wide font-bold text-slate-500">Specificații tehnice</div>
          <div className="grid grid-cols-3 gap-3">
            <Input type="number" placeholder="Putere kW" value={form.specs.motor_power_kw} onChange={setSpec("motor_power_kw")} className="rounded-none" />
            <Input type="number" placeholder="Arbore mm" value={form.specs.spindle_diameter_mm} onChange={setSpec("spindle_diameter_mm")} className="rounded-none" />
            <Input type="number" placeholder="Greutate kg" value={form.specs.weight_kg} onChange={setSpec("weight_kg")} className="rounded-none" />
            <Input type="number" placeholder="Turație rpm" value={form.specs.max_rpm} onChange={setSpec("max_rpm")} className="rounded-none" />
            <Input type="number" placeholder="An" value={form.specs.year} onChange={setSpec("year")} className="rounded-none" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={save} disabled={loading} className="bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-3 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="form-save">
            {loading ? "Se salvează..." : "Salvează"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
